/*
  NUDIENT: Varukorgslådan, paket C1, M-077. Horizons låda, snippets/cart-drawer.liquid
  och assets/theme-drawer.js, öppnar, stänger, räknar och ritar om lådan.
  Det här skriptet gör bara det Horizon saknar:

  - Hämtar korten i WE RECOMMEND, Cart/Recommend 317:366, och RECENTLY VIEWED,
    Cart/RecentlyViewed 319:282, från sections/nudient-cart-products.liquid med
    Shopifys sektionsrendering, bara när lådan är öppen, M-077 punkt 5 och 10.
    - WE RECOMMEND: Shopifys rekommendationer för första radens produkt,
      intent complementary och, om det är tomt, related.
    - RECENTLY VIEWED: Horizons lista över senast visade produkter,
      assets/recently-viewed-products.js, genom sökningen, som Horizons
      assets/predictive-search.js gör.
    Korten hämtas om efter varje ändring i varukorgen. Behållarna har
    data-skip-node-update, så att Horizons omritning inte tömmer dem.
  - Lägger kortens ADD i varukorgen med samma anrop och samma händelse som
    Horizons assets/product-form.js, CartLinesUpdateEvent, så att Horizon ritar
    om lådan och sidhuvudets varukorg.
  - Skriver om SHOP ALL CASES i den tomma lådan till kundens valda modells
    kollektion, som menylänken i B2c. Utan eget val, eller när modellen saknar
    egen kollektion, går länken till phone-cases, M-007, M-020 och M-023.

  - Stänger lådan vid tryck utanför från 990, där Horizon inte gör det.
  - Sätter om Horizons fokusfälla när korten har kommit och när lådan ritats
    om, från 990, där lådan inte är modal, så att Tab når allt i lådan.

  Rekommendationernas modellfilter sitter i sections/nudient-cart-products.liquid.

  Värden som inte är tokens:
  - nudient-cart-products: sektionen som ritar korten.
  - complementary och related: Shopifys två sorters rekommendationer.
  - 10: hur många rekommendationer som hämtas, så att två finns kvar efter att
    varukorgens produkter och slutsålda tagits bort i sektionen.
  - 1: ADD lägger en produkt i varukorgen.
  - cart-drawer-section: Horizons sektion för lådan, som ritas om efter ADD.
*/

import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { findModel, getModel } from '@nudient/model-store';
import { trapFocus } from '@theme/focus';

const SECTION = 'nudient-cart-products';

const drawer = () => document.querySelector('theme-drawer#cart-drawer');
const isOpen = () => {
  const element = drawer();
  return Boolean(element && (element.hasAttribute('open') || element.querySelector('dialog')?.open));
};

const root = () => window.Shopify?.routes?.root || '/';

/** Hämtar sektionen och returnerar dess kort, eller null. */
const fetchCards = async (url) => {
  const response = await fetch(url, { headers: { Accept: 'text/html' } });
  if (!response.ok) return null;
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const list = doc.querySelector('[data-nudient-cart-products]');
  if (!list || list.children.length === 0) return null;
  return list.innerHTML;
};

const recommendUrl = (productId, intent) => {
  const url = new URL(`${root()}recommendations/products`, location.origin);
  url.searchParams.set('product_id', productId);
  url.searchParams.set('limit', '10');
  url.searchParams.set('intent', intent);
  url.searchParams.set('section_id', SECTION);
  return url;
};

const recentUrl = (ids) => {
  const url = new URL(window.Theme?.routes?.search_url || `${root()}search`, location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('section_id', SECTION);
  return url;
};

/** Fyller ett rutnät, om det inte redan är fyllt för samma innehåll. */
const load = async (grid) => {
  const kind = grid.getAttribute('data-nudient-cart-grid');
  const wrapper = grid.closest(kind === 'recommend' ? '[data-nudient-cart-recommend]' : '[data-nudient-cart-recent]');
  if (!wrapper) return;

  let key = '';
  let urls = [];
  if (kind === 'recommend') {
    const source = wrapper.getAttribute('data-nudient-cart-source');
    if (!source) return;
    key = `recommend:${source}`;
    urls = [recommendUrl(source, 'complementary'), recommendUrl(source, 'related')];
  } else {
    const ids = RecentlyViewed.getProducts();
    key = `recent:${ids.join(',')}`;
    urls = ids.length ? [recentUrl(ids)] : [];
  }

  if (grid.dataset.nudientKey === key) return;
  grid.dataset.nudientKey = key;

  let cards = null;
  for (const url of urls) {
    try {
      cards = await fetchCards(url);
    } catch {
      cards = null;
    }
    if (cards) break;
  }
  if (grid.dataset.nudientKey !== key) return;

  grid.innerHTML = cards || '';
  wrapper.hidden = !cards;
  retrap();
};

/*
  Från 990 är lådan inte modal, och Horizons fokusfälla, trapFocus i
  assets/focus.js, räknar sina element när lådan öppnas. Kort som kommer
  efteråt, och rader som Horizon ritar om, når den då inte. Fällan sätts därför
  om med samma funktion när innehållet ändrats, och fokus stannar där den var,
  M-073.
*/
const retrap = () => {
  const dialog = drawer()?.querySelector('dialog');
  if (!dialog?.open || dialog.matches(':modal')) return;
  const active = document.activeElement;
  trapFocus(dialog);
  if (active instanceof HTMLElement && dialog.contains(active)) active.focus();
};

const pointShopLink = () => {
  const model = findModel(getModel());
  document.querySelectorAll('[data-nudient-cart-shop]').forEach((link) => {
    const generic = link.getAttribute('data-nudient-generic-url') || link.getAttribute('href') || '';
    const target = model?.url || generic;
    if (target && link.getAttribute('href') !== target) link.setAttribute('href', target);
  });
};

let scheduled = false;
const refresh = () => {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    pointShopLink();
    if (!isOpen()) return;
    drawer()
      ?.querySelectorAll('[data-nudient-cart-grid]')
      .forEach((grid) => load(grid));
  });
};

/** Glömmer vad rutnäten visar, så att de hämtas om. */
const invalidate = () => {
  drawer()
    ?.querySelectorAll('[data-nudient-cart-grid]')
    .forEach((grid) => {
      delete grid.dataset.nudientKey;
    });
  refresh();
};

/** ADD på ett kort, samma anrop och händelse som Horizons product-form.js. */
const add = async (button) => {
  const variantId = button.getAttribute('data-nudient-cart-add');
  if (!variantId || button.disabled) return;
  button.disabled = true;

  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
    .filter(Boolean);
  if (!sectionIds.includes('cart-drawer-section')) sectionIds.push('cart-drawer-section');

  const body = new FormData();
  body.set('id', variantId);
  body.set('quantity', '1');
  body.set('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || `${root()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const result = await response.json();
    const cart = await (await fetch(`${window.Theme?.routes?.cart_url || `${root()}cart`}.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'nudient-cart',
        itemCount: 1,
        sections: result.sections,
        didError: Boolean(result.status),
      },
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    button.disabled = false;
  }
};

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-nudient-cart-add]') : null;
  if (button instanceof HTMLButtonElement) add(button);
});

/*
  Tryck utanför lådan från 990. Horizon stänger bara vid tryck på bakgrunden i
  modalt läge, assets/theme-drawer.js #onBackdropClick. Från 990 ligger den
  mörka ytan över sidan, och ett tryck där stänger lådan med Horizons close(),
  utan att trycket når länken under, planen punkt 8 och 10.
*/
document.addEventListener(
  'click',
  (event) => {
    const element = drawer();
    const dialog = element?.querySelector('dialog');
    if (!element || !dialog?.open || dialog.matches(':modal')) return;
    if (!(event.target instanceof Node) || dialog.contains(event.target)) return;
    if (event.target instanceof Element && event.target.closest('[aria-controls="cart-drawer"]')) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof element.close === 'function') element.close();
  },
  true
);

document.addEventListener(
  'theme-drawer:open',
  () => {
    refresh();
  },
  true
);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const after = () => {
    invalidate();
    requestAnimationFrame(retrap);
  };
  event.promise?.then(after, after);
});

document.addEventListener('model:change', pointShopLink);

const start = () => {
  pointShopLink();
  const element = drawer();
  if (element) new MutationObserver(refresh).observe(element, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  refresh();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
